// Single source of truth for event names. Always reference events via
// EVENTS.X at call sites instead of the raw string literal.
export const EVENTS = {
  PAGE_VIEW: 'page_view',
  SELF_VALIDATION_ERROR: 'self_validation_error',
  VENDOR_PORTAL_ENTER: 'vendor_portal_enter',
  VENDOR_PORTAL_BLOCKED_PAGE: 'vendor_portal_blocked_page',
  VENDOR_PORTAL_GET_STARTED_CTA: 'vendor_portal_get_started_cta',
  VENDOR_PORTAL_PAYEE_DETAILS_ERROR: 'vendor_portal_payee_details_error',
  VENDOR_PORTAL_PAYEE_DETAILS_COMPLETED: 'vendor_portal_payee_details_completed',
  VENDOR_PORTAL_ACCOUNT_DETAILS_ERROR: 'vendor_portal_account_details_error',
  VENDOR_PORTAL_ACCOUNT_DETAILS_COMPLETED: 'vendor_portal_account_details_completed',
  VENDOR_PORTAL_FINICITY_SHOWN: 'vendor_portal_finicity_shown',
  VENDOR_PORTAL_FINICITY_CLICK_START: 'vendor_portal_finicity_click_start',
  VENDOR_PORTAL_FINICITY_CLICK_MINI_PAYMENT: 'vendor_portal_finicity_click_mini_payment',
  VENDOR_PORTAL_INSTRUCTIONS_SHOWN: 'vendor_portal_instructions_shown',
  VENDOR_PORTAL_INSTRUCTIONS_DOWNLOAD: 'vendor_portal_instructions_download',
  VALIDATION_START: 'validation_start',
} as const;

export type EventName = typeof EVENTS[keyof typeof EVENTS];

export type UtmParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_content?: string;
  utm_campaign?: string;
};

// Every vendor-portal event carries the captured UTM attribution params, so
// they're part of the event type contract (& Partial<UtmParams>) — a typo or
// shape change in UtmParams is caught at call sites rather than silently
// dropped by TS's relaxed excess-property check for spreads.
export type VendorPortalEvent = {
  is_brand_page: boolean;
  is_ips_mode: boolean;
  ref_id?: string;
} & Partial<UtmParams>;

// Per-event payload types. Useful when you need to type a function or
// component prop that carries a specific event.
export type PageViewEvent = {
  name: typeof EVENTS.PAGE_VIEW;
  page_location: string;
  page_title: string;
};

export type SelfValidationErrorEvent = {
  name: typeof EVENTS.SELF_VALIDATION_ERROR;
  error_message: string;
  payee_country?: string;
};

export type VendorPortalEnterEvent = {
  name: typeof EVENTS.VENDOR_PORTAL_ENTER;
} & VendorPortalEvent;

export type VendorPortalBlockedPageEvent = {
  name: typeof EVENTS.VENDOR_PORTAL_BLOCKED_PAGE;
} & Partial<UtmParams>;

export type VendorPortalGetStartedCtaEvent = {
  name: typeof EVENTS.VENDOR_PORTAL_GET_STARTED_CTA;
} & VendorPortalEvent;

export type VendorPortalPayeeDetailsErrorEvent = {
  name: typeof EVENTS.VENDOR_PORTAL_PAYEE_DETAILS_ERROR;
  error_message: string;
} & VendorPortalEvent;

export type VendorPortalPayeeDetailsCompletedEvent = {
  name: typeof EVENTS.VENDOR_PORTAL_PAYEE_DETAILS_COMPLETED;
  country_code: string;
} & VendorPortalEvent;

export type VendorPortalAccountDetailsErrorEvent = {
  name: typeof EVENTS.VENDOR_PORTAL_ACCOUNT_DETAILS_ERROR;
  error_message: string;
} & VendorPortalEvent;

export type VendorPortalAccountDetailsCompletedEvent = {
  name: typeof EVENTS.VENDOR_PORTAL_ACCOUNT_DETAILS_COMPLETED;
  account_country_code: string;
} & VendorPortalEvent;

export type VendorPortalFinicityShownEvent = {
  name: typeof EVENTS.VENDOR_PORTAL_FINICITY_SHOWN;
} & Partial<UtmParams>;

export type VendorPortalFinicityClickStartEvent = {
  name: typeof EVENTS.VENDOR_PORTAL_FINICITY_CLICK_START;
  ref_id?: string;
} & Partial<UtmParams>;

export type VendorPortalFinicityClickMiniPaymentEvent = {
  name: typeof EVENTS.VENDOR_PORTAL_FINICITY_CLICK_MINI_PAYMENT;
  ref_id?: string;
} & Partial<UtmParams>;

export type VendorPortalInstructionsShownEvent = {
  name: typeof EVENTS.VENDOR_PORTAL_INSTRUCTIONS_SHOWN;
  instructions_type: string;
  ref_id?: string;
} & Partial<UtmParams>;

export type VendorPortalInstructionsDownloadEvent = {
  name: typeof EVENTS.VENDOR_PORTAL_INSTRUCTIONS_DOWNLOAD;
  ref_id?: string;
  instructions_type: string;
} & Partial<UtmParams>;

export type ValidationStartEvent = {
  name: typeof EVENTS.VALIDATION_START;
  validation_type: 'knox verify';
};

// Union of all events — what trackEvent accepts.
export type AnalyticsEvent =
  | PageViewEvent
  | SelfValidationErrorEvent
  | VendorPortalEnterEvent
  | VendorPortalBlockedPageEvent
  | VendorPortalGetStartedCtaEvent
  | VendorPortalPayeeDetailsErrorEvent
  | VendorPortalPayeeDetailsCompletedEvent
  | VendorPortalAccountDetailsErrorEvent
  | VendorPortalAccountDetailsCompletedEvent
  | VendorPortalFinicityShownEvent
  | VendorPortalFinicityClickStartEvent
  | VendorPortalFinicityClickMiniPaymentEvent
  | VendorPortalInstructionsShownEvent
  | VendorPortalInstructionsDownloadEvent
  | ValidationStartEvent;
